import React, { useEffect, useRef, useCallback } from 'react';
import { useConsoleStore } from '@/stores/console';
import { useUiStore } from '@/stores/ui';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, PlayCircle, StopCircle, RefreshCw, Terminal } from 'lucide-react';
import { Terminal as XTerm } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';
const DevSessionPage = () => {
  const { status, isLoading, fetchStatus, startSession, stopSession, connect, disconnect, sendPaneInput, resizePane } = useConsoleStore((state) => ({
    status: state.status,
    isLoading: state.isLoading,
    fetchStatus: state.fetchStatus,
    startSession: state.startSession,
    stopSession: state.stopSession,
    connect: state.connect,
    disconnect: state.disconnect,
    sendPaneInput: state.sendPaneInput,
    resizePane: state.resizePane,
  }));
  const setHeaderTitle = useUiStore((state) => state.setHeaderTitle);
  const terminalRefs = useRef<Record<string, { terminal: XTerm; fitAddon: FitAddon }>>({});
  const lastContentRef = useRef<Record<string, string>>({});
  const resizeObserversRef = useRef<Record<string, ResizeObserver>>({});

  useEffect(() => {
    setHeaderTitle('Development Session');
    fetchStatus();
    connect();
    return () => disconnect();
  }, [setHeaderTitle, connect, disconnect, fetchStatus]);

  // Initialize terminals when status changes
  useEffect(() => {
    if (status?.isRunning && status.panes) {
      status.panes.forEach(pane => {
        if (!terminalRefs.current[pane.id]) {
          // Create new terminal for this pane
          const terminal = new XTerm({
            rows: 24,
            cols: 80,
            scrollback: 10000,
            allowTransparency: true,
            // IMPORTANT: handle Windows line endings correctly so Enter actually advances
            convertEol: true,
            // Don't snap to bottom on the first keystroke if the user isn't at bottom yet
            scrollOnUserInput: false,
            // // Helps CR/LF, backspace, etc. on Windows PTY
            windowsMode: true,
            // Keep spacing compact but reliable for fit math
            lineHeight: 1,
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            fontSize: 12,
            cursorBlink: true,
            theme: {
              background: '#1e1e1e',
              foreground: '#d4d4d4',
            },
          });

          const fitAddon = new FitAddon();
          terminal.loadAddon(fitAddon);

          terminalRefs.current[pane.id] = { terminal, fitAddon };
          lastContentRef.current[pane.id] = '';

          terminal.onData((data) => {
            sendPaneInput(pane.id, data);
          });

          terminal.onResize(({ cols, rows }) => {
            if (cols > 0 && rows > 0) {
              resizePane(pane.id, rows, cols);
            }
          });

          // Track whether user is at bottom; only auto-stick if they are.
          let atBottom = true;
          terminal.onScroll((_y) => {
            const b = terminal.buffer.active;
            atBottom = (b.viewportY === b.baseY);
          });

          // expose helper for later writes
          // @ts-ignore
          (terminal as any).__atBottom = () => atBottom;
        }

        // Update content incrementally
        const termData = terminalRefs.current[pane.id];
        const lastContent = lastContentRef.current[pane.id] || '';

        // Only write new content once the terminal is attached/opened
        if (termData?.terminal.element && pane.content != null && pane.content !== lastContent) {
          const term = termData.terminal;
          const wasAtBottom = (term as any).__atBottom ? (term as any).__atBottom() : true;
          if (pane.content.length < lastContent.length) {
            // buffer reset
            term.reset();
            term.clear();
            term.write(pane.content, () => { if (wasAtBottom) term.scrollToBottom(); });
          } else {
            const newChunk = pane.content.slice(lastContent.length);
            if (newChunk) {
              term.write(newChunk, () => { if (wasAtBottom) term.scrollToBottom(); });
            }
            }
          lastContentRef.current[pane.id] = pane.content;
        }
      });
    }
  }, [status, resizePane, sendPaneInput]);

// Fit terminals on resize
  useEffect(() => {
    const handleResize = () => {
      Object.values(terminalRefs.current).forEach(termObj => {
        termObj.fitAddon.fit();
      });
    };

    window.addEventListener('resize', handleResize);
    // Initial fit
    setTimeout(handleResize, 100);

    return () => {
      window.removeEventListener('resize', handleResize);
      Object.values(resizeObserversRef.current).forEach(ro => ro.disconnect());
      resizeObserversRef.current = {};
      // Dispose terminals
      Object.values(terminalRefs.current).forEach(termObj => {
        termObj.terminal.dispose();
      });
      terminalRefs.current = {};
      lastContentRef.current = {};
    };
  }, []);

  const renderContent = () => {
    if (isLoading && !status) {
      return (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="ml-2">Loading session status...</p>
        </div>
      );
    }

    if (!status?.isRunning) {
      return (
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle>Start Development Session</CardTitle>
            <CardDescription>
              This will start the frontend and backend servers in shared console windows.
              Output will be streamed directly to this interface.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={startSession} disabled={isLoading}>
              <PlayCircle className="mr-2 h-4 w-4 shrink-0" />
              Start Session
            </Button>
          </CardFooter>
        </Card>
      );
    }

    return (
      <div className="space-y-4 h-full flex flex-col overflow-auto">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">
            Session: <span className="font-mono bg-muted p-1 rounded">{status.sessionName}</span>
          </h2>
          <div className="flex gap-2">
             <Button variant="outline" onClick={fetchStatus} disabled={isLoading} size="sm">
                <RefreshCw className={`mr-2 h-4 w-4 shrink-0 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
            </Button>
            <Button variant="destructive" onClick={stopSession} disabled={isLoading} size="sm">
              <StopCircle className="mr-2 h-4 w-4 shrink-0" />
              Stop Session
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">
          {status.panes?.map(pane => (
            <Card key={pane.id} className="flex flex-col h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 leading-none overflow-visible">
                  <span className="inline-flex h-5 w-5 items-center justify-center shrink-0 overflow-visible">
                    <Terminal className="h-4 w-4" />
                  </span>
                  {pane.title || pane.id}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 min-h-0 p-0">
                <div 
                  ref={(el) => {
                    if (el && terminalRefs.current[pane.id] && el.children.length === 0) {
                      // Attach terminal to DOM, then fit and write initial content.
                      const { terminal, fitAddon } = terminalRefs.current[pane.id];
                      terminal.open(el);
                      // Keep overflow behavior inside xterm; parent should not hide it.
                      // (Avoid clipping and let xterm manage its own viewport.)
                      el.style.overflow = 'visible';
                      // Observe container size changes for precise fitting
                      const ro = new ResizeObserver(() => fitAddon.fit());
                      ro.observe(el);
                      resizeObserversRef.current[pane.id] = ro;

                      // Fit after mount, after next frame, and after fonts are ready
                      fitAddon.fit();
                      requestAnimationFrame(() => fitAddon.fit());
                      // Some browsers support font loading events
                      // @ts-ignore
                      if (document.fonts && document.fonts.ready) {
                        // @ts-ignore
                        document.fonts.ready.then(() => fitAddon.fit()).catch(() => {});
                      }

                      // Write the initial pane content ONLY after open+fit
                      const initial = (status?.panes?.find(p => p.id === pane.id)?.content) ?? '';
                      const wasAtBottom = true;
                      terminal.clear();
                      if (initial) {
                        terminal.write(initial, () => { if (wasAtBottom) terminal.scrollToBottom(); });
                      }
                      lastContentRef.current[pane.id] = initial || '';
                    }
                  }}
                  className="h-full w-full bg-black"
                />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto p-4 lg:p-6 h-full">
      {renderContent()}
    </div>
  )
};

export default DevSessionPage;